import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { statusToneTokens, type StatusTone } from '../../constants/statusTokens';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { getChartTokens } from '../../theme/chartTheme';
import { contentBorders } from '../../theme/createContentTheme';

export interface OrderStatusSlice {
  key: string;
  label: string;
  value: number;
  tone: StatusTone;
}

const COMPLETED_FILL = { light: '#16A34A', dark: '#4ADE80' } as const;

interface OrderStatusDonutProps {
  slices: OrderStatusSlice[];
  /** Total pickup orders (may exceed the sum of slices when other statuses exist). */
  total: number;
  ariaLabel: string;
}

/**
 * Pickup orders by status as a donut with a text legend (count and share), so
 * the split reads at a glance without relying on colour alone.
 */
const OrderStatusDonut: React.FC<OrderStatusDonutProps> = ({ slices, total, ariaLabel }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const tokens = useMemo(() => getChartTokens(theme), [theme]);
  const borders = contentBorders(theme);
  const mode = theme.palette.mode;
  const visible = slices.filter((s) => s.value > 0);
  const fill = (tone: StatusTone) => {
    // Completed orders read as green here at the product owner's request — a
    // deliberate exception to the content area's no-green palette.
    if (tone === 'complete') return COMPLETED_FILL[mode];
    const f = statusToneTokens(mode, tone).fill;
    return f === 'transparent' ? borders.strong : f;
  };
  const summary = slices.map((s) => `${s.label}: ${fmt.number(s.value)}`).join(', ');

  return (
    <Box
      sx={{
        display: 'grid',
        // The legend stays next to the donut so counts read with their labels.
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '168px minmax(0, 420px)' },
        alignItems: 'center',
        gap: { xs: 2, sm: 3 },
        minWidth: 0,
      }}
    >
      <Box role="img" aria-label={`${ariaLabel}. ${summary}`} sx={{ position: 'relative', width: 168, height: 168, mx: { xs: 'auto', sm: 0 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={visible.length > 0 ? visible : [{ key: 'empty', label: '', value: 1, tone: 'offline' as StatusTone }]}
              dataKey="value"
              nameKey="label"
              innerRadius="66%"
              outerRadius="96%"
              paddingAngle={visible.length > 1 ? 1.5 : 0}
              stroke={theme.palette.background.paper}
              strokeWidth={2}
              startAngle={90}
              endAngle={-270}
              {...tokens.animation}
            >
              {(visible.length > 0 ? visible : [null]).map((s, i) => (
                <Cell key={s?.key ?? `empty-${i}`} fill={s ? fill(s.tone) : borders.subtle} />
              ))}
            </Pie>
            {visible.length > 0 ? (
              <Tooltip
                contentStyle={tokens.tooltip.contentStyle}
                itemStyle={tokens.tooltip.itemStyle}
                formatter={(value: number, name: string) => [fmt.number(value), name]}
                separator=": "
              />
            ) : null}
          </PieChart>
        </ResponsiveContainer>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              <bdi>{fmt.number(total)}</bdi>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.lead.ordersShort')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, minWidth: 0 }}>
        {slices.map((s, index) => (
          <Box
            component="li"
            key={s.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: '12px minmax(0, 1fr) auto auto',
              alignItems: 'center',
              columnGap: 1.25,
              py: 1,
              borderTop: index === 0 ? 0 : `1px solid ${borders.subtle}`,
            }}
          >
            <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: fill(s.tone) }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {s.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', textAlign: 'end' }}>
              <bdi>{fmt.number(s.value)}</bdi>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 44, textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}
            >
              <bdi>{fmt.share(total > 0 ? s.value / total : null)}</bdi>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default OrderStatusDonut;
